import {
    Button,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@repo/shadcn';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { ChevronDownIcon, Loader2Icon, Trash } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { ButtonGroup } from '@components/ui/button';

import { Route } from '@/constants';
import { useDeletePrePurchaseByIdMutation } from '@/features/slice/purchases';
import { toastError } from '@/utils';

const PrePurchaseActionCell = ({ row }: CellContext<any, unknown>) => {
    const [deletePrePurchase, { isLoading: isDeleting }] =
        useDeletePrePurchaseByIdMutation();

    const handleDeletePrePurchase = async (id: string) => {
        try {
            await deletePrePurchase(id).unwrap();
            toast.success('Pre purchase deleted successfully.');
        } catch (error) {
            toastError(error, 'PrePurchaseActionCell: handleDeletePrePurchase');
        }
    };

    return (
        <div className={'flex justify-end'}>
            <ButtonGroup>
                <Link to={`${Route.PRE_PURCHASES}/${row.original.id}`}>
                    <Button type={'button'} variant={'outline'}>
                        View Details
                    </Button>
                </Link>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="!pl-2">
                            <ChevronDownIcon />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        align="end"
                        className="[--radius:1rem]"
                    >
                        <DropdownMenuGroup>
                            <DropdownMenuItem
                                className={'flex gap-2 text-destructive'}
                                onClick={() =>
                                    handleDeletePrePurchase(row.original.id)
                                }
                            >
                                {isDeleting ? (
                                    <Loader2Icon className={'animate-spin'} />
                                ) : (
                                    <>
                                        <Trash className={'size-4'} />
                                        Delete
                                    </>
                                )}
                            </DropdownMenuItem>
                        </DropdownMenuGroup>
                    </DropdownMenuContent>
                </DropdownMenu>
            </ButtonGroup>
        </div>
    );
};

export const prePurchaseListColumns: Array<ColumnDef<any>> = [
    {
        accessorKey: 'id',
        header: 'SL.No.',
        cell: ({ row }) => {
            return <div>{row.index + 1}</div>;
        },
    },

    {
        accessorKey: 'supplier_name',
        header: 'Supplier',
        cell: ({ row }) => {
            return <div>{row.original.supplier_name}</div>;
        },
    },

    {
        accessorKey: 'brand_name',
        header: 'Brand',
        cell: ({ row }) => {
            return <div>{row.original.brand_name}</div>;
        },
    },

    {
        accessorKey: 'note',
        header: 'Note',
        cell: ({ row }) => {
            return <div>{row.original.note}</div>;
        },
    },

    {
        accessorKey: 'created_at',
        header: 'Created At',
        cell: ({ row }) => {
            return <div>{format(row.original.created_at, 'dd/MM/yyyy')}</div>;
        },
    },

    {
        accessorKey: 'total_items',
        header: () => <div className={'text-right'}>Total Items</div>,
        cell: ({ row }) => {
            return (
                <div className={'text-right'}>{row.original.total_items}</div>
            );
        },
    },

    {
        accessorKey: 'actions',
        header: () => <div className={'text-right'}>Actions</div>,
        cell: PrePurchaseActionCell,
    },
];
